import { SvgIcon } from '@mui/material'

// Small shared icon components with no natural home of their own — pulled
// out of whichever file first needed them once a second file needed the same
// one too, rather than duplicating the inline SVG.

// RichReplyEditor's "Insert image" / "Add attachment" toolbar controls used
// to be emoji glyphs inside a Typography, which is why sizing/color props
// never had any real effect on them. These wrap MUI's own SvgIcon (the same
// primitive @mui/icons-material itself is built on — @mui/icons-material is
// not a dependency here, this codebase draws its own icons, see the
// feather-icons-style outline set below) so `fontSize`/`color`/`sx` behave
// exactly like a real MUI icon component.
export function ImageIcon({ sx, ...props }) {
  return (
    <SvgIcon
      {...props}
      viewBox="0 0 24 24"
      sx={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', ...sx }}
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </SvgIcon>
  )
}

// ScheduleWidget's manual "refresh my schedule now" button -- same
// feather-icons-style outline set as the rest of this file (refresh-cw).
export function RefreshIcon({ sx, ...props }) {
  return (
    <SvgIcon
      {...props}
      viewBox="0 0 24 24"
      sx={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', ...sx }}
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </SvgIcon>
  )
}

export function AttachmentIcon({ sx, ...props }) {
  return (
    <SvgIcon
      {...props}
      viewBox="0 0 24 24"
      sx={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', ...sx }}
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </SvgIcon>
  )
}

// Expand/collapse (maximize-2/minimize-2 style) icon. Originally defined
// inline in SuggestionPanel.jsx (same hand-rolled convention as
// SpamRuleDialog.jsx's own separate copy, for its Body pattern field's size
// toggle) — extracted here once ManualActionsSidebar.jsx needed the same
// expand/collapse control for its own note textarea.
export function ExpandIcon({ expanded }) {
  return expanded ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 14 10 14 10 20" /><polyline points="20 10 14 10 14 4" />
      <line x1="14" y1="10" x2="21" y2="3" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9" /><polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" /><line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  )
}

// Chevron (feather chevron-down), for a collapsible section's header. Points
// down when expanded; pass expanded={false} to rotate it to point right.
export function ChevronIcon({ expanded = true, sx, ...props }) {
  return (
    <SvgIcon
      {...props}
      viewBox="0 0 24 24"
      sx={{ fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', transition: 'transform 150ms', transform: expanded ? 'none' : 'rotate(-90deg)', ...sx }}
    >
      <polyline points="6 9 12 15 18 9" />
    </SvgIcon>
  )
}

// The nav rail's "Parked" entry (2026-10-02): a car, in the same feather-style
// outline as QueueIcon/ClosedIcon (stroke="currentColor", so it inherits the
// rail's text colour).
export function CarIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17H3v-5l2-5h14l2 5v5h-2" />
      <path d="M5 12h14" />
      <circle cx="7.5" cy="17" r="2" />
      <circle cx="16.5" cy="17" r="2" />
      <path d="M9.5 17h5" />
    </svg>
  )
}

// --- App.jsx's left icon nav rail — five hand-rolled icons, same feather-
// icons-style convention as ExpandIcon above (stroke="currentColor" so each
// just inherits the rail's own text color, no per-icon color prop needed). ---

// Inbox tray (the open queue) with a small clock face in the corner — the
// "not yet closed" / awaiting-action state, as distinct from ClosedIcon below.
// Single-color outline throughout (no fill), same as every other icon here,
// so it inherits the rail's own text color automatically.
export function QueueIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 15 8 13 11 9 11 7 8 1 8" />
      <path d="M4.6 3.1 1 8v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8l-3.6-4.9A2 2 0 0 0 13.8 2h-7.6a2 2 0 0 0-1.6 1.1z" />
      <circle cx="18.5" cy="17.5" r="3.2" strokeWidth="1.6" />
      <path d="M18.5 16v1.5l1 1" strokeWidth="1.4" />
    </svg>
  )
}

// The "New" email (2026-10-08): an envelope with a plus badge at its lower
// right, the same badge-over-shape layout as QueueIcon's clock and
// ClosedIcon's magnifier.
export function NewEmailIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 18H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v7" />
      <polyline points="22 6 12 13 2 6" />
      <circle cx="18.5" cy="17.5" r="3.6" strokeWidth="1.6" />
      <path d="M18.5 15.6v3.8M16.6 17.5h3.8" strokeWidth="1.6" />
    </svg>
  )
}

// Tasks (2026-10-12): MUI's PlaylistAdd glyph (a list with a plus), drawn
// here from its own path because @mui/icons-material is not a dependency.
// Filled, like the MUI icon, at the nav rail's 28px.
export function TasksIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
      <path d="M14 10H3v2h11v-2zm0-4H3v2h11V6zm4 8v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zM3 16h7v-2H3v2z" />
    </svg>
  )
}

// A flat, front-facing carton with its top flaps folded down to a peak (not
// an isometric/3D box) plus a magnifying glass over it — "already handled,
// look one up if you need it," as distinct from QueueIcon's still-open tray
// above.
export function ClosedIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="4" y="9" width="16" height="12" />
      <path d="M4 9 9 4h6l5 5" />
      <line x1="4" y1="13" x2="20" y2="13" strokeWidth="1.4" />
      <circle cx="16.5" cy="16.5" r="2.7" strokeWidth="1.6" />
      <line x1="18.5" y1="18.5" x2="20.7" y2="20.7" strokeWidth="1.6" />
    </svg>
  )
}

// Standard gear/cog — unchanged from the feather-icons "settings" glyph
// already used elsewhere in this codebase's own hand-rolled style.
export function SettingsIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

// A cloud with the literal "API" text inside it, and a single stem branching
// out (fanning to three distinct node circles) underneath — an API/
// integrations distribution glyph for the Ops section, not parallel drip
// lines. The "API" text is a deliberate, narrow exception to this file's
// otherwise text-free convention — small solid text needs fill, not just an
// outline stroke, to stay legible at this size.
// Audit: a clipboard with a check mark (the QA auditor, admin only).
export function AuditIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M9 4V3h6v1" />
      <path d="m8.5 13 2.5 2.5 4.5-5" />
    </svg>
  )
}

export function OpsIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 13H18a4 4 0 0 0 0-8 5.5 5.5 0 0 0-10.5 1.5A4.5 4.5 0 0 0 6.5 15h1" />
      <text x="12" y="10.5" textAnchor="middle" fontSize="6" fontWeight="bold" fontFamily="sans-serif" fill="currentColor" stroke="none">API</text>
      <path d="M12 15v2" strokeWidth="1.8" />
      <path d="M12 17 7 20.5M12 17v3M12 17l5 3.5" strokeWidth="1.6" />
      <circle cx="7" cy="21" r="1.3" strokeWidth="1.4" />
      <circle cx="12" cy="21.3" r="1.3" strokeWidth="1.4" />
      <circle cx="17" cy="21" r="1.3" strokeWidth="1.4" />
    </svg>
  )
}

// A line chart: an axis (vertical up the left edge, horizontal along the
// bottom, meeting at the corner) with a rising polyline connecting 4 visible
// plotted points trending up-right.
export function ReportingIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 3 3 21 21 21" />
      <polyline points="5 17 10 13 15 9 20 5" strokeWidth="1.8" />
      <circle cx="5" cy="17" r="1.3" strokeWidth="1.5" />
      <circle cx="10" cy="13" r="1.3" strokeWidth="1.5" />
      <circle cx="15" cy="9" r="1.3" strokeWidth="1.5" />
      <circle cx="20" cy="5" r="1.3" strokeWidth="1.5" />
    </svg>
  )
}

// Schedule Hub's calendar+checkmark mark, hand-drawn to match the outline
// convention every other icon in this file uses -- a raster copy of the
// real logo was tried first, but its baked-in white background (no alpha
// channel) always read as a visible box against the rail's flat color, and
// recoloring a raster asset to currentColor isn't possible the way it is
// for an inline SVG. This is a plain white outline on the rail, exactly
// like QueueIcon/ClosedIcon/etc, not a faithful brand-color reproduction.
export function ScheduleHubIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="5" width="15" height="15" rx="1.5" />
      <line x1="3" y1="10" x2="18" y2="10" />
      <line x1="7" y1="2" x2="7" y2="6" strokeWidth="1.8" />
      <line x1="14" y1="2" x2="14" y2="6" strokeWidth="1.8" />
      <rect x="6.3" y="13" width="1.6" height="1.6" strokeWidth="1.3" />
      <rect x="10.7" y="13" width="1.6" height="1.6" strokeWidth="1.3" />
      <circle cx="18.5" cy="18.5" r="4.2" strokeWidth="1.8" />
      <path d="M16.6 18.6 18 20l2.3-2.6" strokeWidth="1.6" />
    </svg>
  )
}
